import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import {
  Building2, Users, CheckCircle2, AlertTriangle,
  RefreshCw, Clock, ShieldAlert, Zap, FileText,
  TrendingUp, Bell, Settings, KeyRound,
} from "lucide-react";
import apiService from "../services/api";
import "./SystemHubDashboard.css";

/* ─── Animated counter ───────────────────────────────────────────────────── */
function useCounter(target, delay = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target && target !== 0) return;
    let raf;
    const dur   = 1200;
    const start = performance.now() + delay;
    const tick  = (now) => {
      if (now < start) { raf = requestAnimationFrame(tick); return; }
      const t = Math.min(1, (now - start) / dur);
      setVal(Math.round((1 - Math.pow(1 - t, 3)) * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, delay]);
  return val;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function licenseColor(pct) {
  if (pct >= 90) return "#EF4444";
  if (pct >= 70) return "#F59E0B";
  return "#10B981";
}

function avatarColor(id) {
  const hue = (id * 47) % 360;
  return { bg: `hsl(${hue},60%,92%)`, fg: `hsl(${hue},55%,38%)` };
}

/* ─── Custom bar tooltip ─────────────────────────────────────────────────── */
const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="shd-tooltip">
      <p className="shd-tooltip__name">{d.nomeCompleto}</p>
      <p className="shd-tooltip__val">
        {d.em_uso} / {d.max} usuários
        <span className="shd-tooltip__pct"> ({d.pct}%)</span>
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
export default function SystemHubDashboard() {
  const [stats,       setStats]       = useState(null);
  const [clients,     setClients]     = useState([]);
  const [pending,     setPending]     = useState([]);
  const [maintenance, setMaintenance] = useState(null);
  const [forensics,   setForensics]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error,       setError]       = useState(false);

  /* ── Fetch usando o axios já configurado com o baseURL do backend ── */
  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    setError(false);

    const token = localStorage.getItem("token");
    const cfg   = { headers: { Authorization: `Bearer ${token}` } };

    const [statsR, clientsR, reqR, maintR, forensicsR] = await Promise.allSettled([
      apiService.get("/api/system-hub/dashboard-stats",                    cfg).then((r) => r.data),
      apiService.get("/api/system-hub/clientes",                           cfg).then((r) => r.data),
      apiService.get("/api/system-hub/licensing-requests?limit=20&page=1", cfg).then((r) => r.data),
      apiService.get("/api/system-hub/maintenance-status",                 cfg).then((r) => r.data),
      apiService.get("/api/system-hub/forensics/analysis",                 cfg).then((r) => r.data),
    ]);

    if (statsR.status     === "fulfilled") setStats(statsR.value);
    if (clientsR.status   === "fulfilled") setClients(clientsR.value || []);
    if (reqR.status       === "fulfilled") {
      const items = reqR.value?.items || [];
      setPending(items.filter((r) => r.status === "pendente"));
    }
    if (maintR.status     === "fulfilled") setMaintenance(maintR.value);
    if (forensicsR.status === "fulfilled") setForensics(forensicsR.value);

    const allFailed = [statsR, clientsR].every((r) => r.status === "rejected");
    if (allFailed) setError(true);

    setLoading(false);
    setRefreshing(false);
    setLastUpdated(new Date());
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Derived data ── */
  const critical = clients.filter(
    (c) => c.max_licencas > 0 && c.licencas_em_uso / c.max_licencas >= 0.9
  );

  const topClients = [...clients]
    .filter((c) => c.max_licencas > 0)
    .map((c) => ({
      nome:        c.nome_empresa?.length > 16 ? c.nome_empresa.slice(0, 16) + "…" : c.nome_empresa,
      nomeCompleto: c.nome_empresa,
      pct:         Math.round((c.licencas_em_uso / c.max_licencas) * 100),
      em_uso:      c.licencas_em_uso,
      max:         c.max_licencas,
    }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 8);

  const alertTotal =
    pending.length + critical.length + (maintenance?.maintenanceMode ? 1 : 0);

  /* ── Animated counters ── */
  const cTotal    = useCounter(stats?.totalClients  ?? 0,   0);
  const cActive   = useCounter(stats?.activeClients ?? 0,  80);
  const cUsers    = useCounter(stats?.totalUsers    ?? 0, 160);
  const cInactive = useCounter(
    (stats?.totalClients ?? 0) - (stats?.activeClients ?? 0), 240
  );

  const now = new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  /* ── KPI definitions ── */
  const kpis = [
    {
      icon:  <Building2    size={20} />,
      value: cTotal,
      label: "Total de Clientes",
      color: "#4F63FF", bg: "#EEF0FF",
    },
    {
      icon:  <CheckCircle2 size={20} />,
      value: cActive,
      label: "Clientes Ativos",
      color: "#10B981", bg: "#D1FAE5",
    },
    {
      icon:  <Users        size={20} />,
      value: cUsers,
      label: "Usuários Cadastrados",
      color: "#8B5CF6", bg: "#EDE9FE",
    },
    {
      icon:  <AlertTriangle size={20} />,
      value: cInactive,
      label: "Clientes Inativos",
      color: "#F59E0B", bg: "#FEF3C7",
    },
  ];

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="shd-page">

      {/* ── HEADER ── */}
      <header className="shd-header">
        <div>
          <p className="shd-header__eyebrow">System Hub · Overview</p>
          <h1 className="shd-header__title">Dashboard</h1>
          <p className="shd-header__date">{now}</p>
        </div>

        <div className="shd-header__actions">
          {maintenance?.maintenanceMode && (
            <span className="shd-badge shd-badge--warn">
              <Settings size={12} />
              Manutenção Ativa
            </span>
          )}

          <span className={`shd-badge ${alertTotal > 0 ? "shd-badge--danger" : "shd-badge--ok"}`}>
            <Bell size={12} />
            {alertTotal > 0
              ? `${alertTotal} item${alertTotal > 1 ? "s" : ""} pendente${alertTotal > 1 ? "s" : ""}`
              : "Tudo OK"}
          </span>

          <button
            className={`shd-refresh${refreshing ? " shd-refresh--spin" : ""}`}
            onClick={fetchAll}
            title="Atualizar"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </header>

      {/* ── ERROR STATE ── */}
      {error && (
        <div className="shd-error">
          <AlertTriangle size={16} />
          Não foi possível carregar os dados. Verifique sua conexão e tente novamente.
        </div>
      )}

      {/* ── KPI ROW ── */}
      <div className="shd-kpi-row">
        {kpis.map((k, i) => (
          <div
            key={i}
            className="shd-kpi"
            style={{ "--kc": k.color, "--kb": k.bg, animationDelay: `${i * 55}ms` }}
          >
            <div className="shd-kpi__icon">{k.icon}</div>
            <div>
              <div className="shd-kpi__value">{loading ? "—" : k.value}</div>
              <div className="shd-kpi__label">{k.label}</div>
            </div>
            <div className="shd-kpi__bar" />
          </div>
        ))}
      </div>

      {/* ── ALERT BANNER ── */}
      {alertTotal > 0 && !loading && (
        <section className="shd-alerts">
          <div className="shd-alerts__head">
            <ShieldAlert size={15} />
            Requer Atenção
            <span className="shd-alerts__count">{alertTotal}</span>
          </div>
          <div className="shd-alerts__list">

            {maintenance?.maintenanceMode && (
              <div className="shd-alert shd-alert--warn">
                <Settings size={13} />
                <span>
                  Sistema em{" "}
                  <strong>
                    manutenção {maintenance.source === "gmud" ? "(GMUD ativa)" : "manual"}
                  </strong>
                </span>
                {maintenance.endDate && (
                  <span className="shd-alert__meta">
                    Previsão de fim:{" "}
                    {new Date(maintenance.endDate).toLocaleString("pt-BR")}
                  </span>
                )}
              </div>
            )}

            {pending.map((r) => (
              <div key={r.id} className="shd-alert shd-alert--info">
                <KeyRound size={13} />
                <span>
                  <strong>{r.nome_empresa}</strong> solicitou ampliação de licenças
                </span>
                <span className="shd-alert__pill">Pendente</span>
              </div>
            ))}

            {critical.map((c) => (
              <div key={c.id} className="shd-alert shd-alert--danger">
                <AlertTriangle size={13} />
                <span>
                  <strong>{c.nome_empresa}</strong> atingiu{" "}
                  {Math.round((c.licencas_em_uso / c.max_licencas) * 100)}% das licenças
                </span>
                <span className="shd-alert__pill shd-alert__pill--red">Crítico</span>
              </div>
            ))}

          </div>
        </section>
      )}

      {/* ── MAIN GRID ── */}
      <div className="shd-grid">

        {/* ── License pressure chart ── */}
        <div className="shd-card shd-card--chart">
          <div className="shd-card__head">
            <div className="shd-card__head-left">
              <span className="shd-card__icon-wrap" style={{ background: "#EEF0FF" }}>
                <TrendingUp size={15} color="#4F63FF" />
              </span>
              <div>
                <h2 className="shd-card__title">Pressão de Licenças</h2>
                <p className="shd-card__sub">Top 8 clientes por % de uso atual</p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="shd-skeleton shd-skeleton--chart" />
          ) : topClients.length === 0 ? (
            <div className="shd-empty">Nenhum cliente com licenças alocadas.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={topClients}
                  layout="vertical"
                  margin={{ top: 0, right: 48, left: 0, bottom: 0 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 10, fill: "#94A3B8" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="nome"
                    width={110}
                    tick={{ fontSize: 11, fill: "#475569" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={<BarTooltip />}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />
                  <Bar dataKey="pct" radius={[0, 6, 6, 0]} barSize={13}>
                    {topClients.map((entry, i) => (
                      <Cell key={i} fill={licenseColor(entry.pct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="shd-legend">
                <span className="shd-legend__item shd-legend__item--green">Normal &lt;70%</span>
                <span className="shd-legend__item shd-legend__item--yellow">Atenção 70–89%</span>
                <span className="shd-legend__item shd-legend__item--red">Crítico ≥90%</span>
              </div>
            </>
          )}
        </div>

        {/* ── Client list ── */}
        <div className="shd-card shd-card--clients">
          <div className="shd-card__head">
            <div className="shd-card__head-left">
              <span className="shd-card__icon-wrap" style={{ background: "#EDE9FE" }}>
                <Building2 size={15} color="#8B5CF6" />
              </span>
              <div>
                <h2 className="shd-card__title">Clientes</h2>
                <p className="shd-card__sub">Status e uso de licenças</p>
              </div>
            </div>
            <span className="shd-count-badge">{clients.length} total</span>
          </div>

          {loading ? (
            <div className="shd-skeleton-list">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="shd-skeleton shd-skeleton--row" />
              ))}
            </div>
          ) : (
            <div className="shd-client-list">
              {clients.slice(0, 7).map((c) => {
                const pct = c.max_licencas > 0
                  ? Math.round((c.licencas_em_uso / c.max_licencas) * 100)
                  : 0;
                const av = avatarColor(c.id);
                return (
                  <div key={c.id} className="shd-client-row">
                    <div
                      className="shd-avatar"
                      style={{ background: av.bg, color: av.fg }}
                    >
                      {c.nome_empresa?.charAt(0).toUpperCase()}
                    </div>
                    <div className="shd-client-row__body">
                      <span className="shd-client-row__name">{c.nome_empresa}</span>
                      <div className="shd-client-row__track-wrap">
                        <div className="shd-client-row__track">
                          <div
                            className="shd-client-row__fill"
                            style={{
                              width:      `${pct}%`,
                              background: licenseColor(pct),
                            }}
                          />
                        </div>
                        <span className="shd-client-row__nums">
                          {c.licencas_em_uso}/{c.max_licencas}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`shd-dot ${
                        c.status === "ativo" ? "shd-dot--ok" : "shd-dot--off"
                      }`}
                      title={c.status}
                    />
                  </div>
                );
              })}

              {clients.length > 7 && (
                <p className="shd-client-more">+{clients.length - 7} outros clientes</p>
              )}
            </div>
          )}
        </div>

        {/* ── Security ── */}
        <div className="shd-card shd-card--security">
          <div className="shd-card__head">
            <div className="shd-card__head-left">
              <span className="shd-card__icon-wrap" style={{ background: "#FFF1F1" }}>
                <ShieldAlert size={15} color="#EF4444" />
              </span>
              <div>
                <h2 className="shd-card__title">Segurança</h2>
                <p className="shd-card__sub">Score e eventos recentes</p>
              </div>
            </div>
          </div>

          {!forensics ? (
            <div className="shd-skeleton shd-skeleton--security" />
          ) : (
            <>
              <div className="shd-score-row">
                <div
                  className="shd-score-ring"
                  style={{ "--score": forensics.stats?.securityScore ?? 100 }}
                >
                  <span className="shd-score-ring__val">
                    {forensics.stats?.securityScore ?? 100}
                  </span>
                </div>
                <div className="shd-score-meta">
                  <div className="shd-score-meta__anomalias">
                    <strong>{forensics.stats?.totalAnomalias ?? 0}</strong>
                    anomalias detectadas
                  </div>
                  <p className="shd-score-meta__audit">
                    Última auditoria:{" "}
                    {forensics.stats?.lastAudit
                      ? new Date(forensics.stats.lastAudit).toLocaleString("pt-BR")
                      : "—"}
                  </p>
                </div>
              </div>

              <div className="shd-sec-events">
                {(forensics.recentAlerts || []).slice(0, 3).map((log) => (
                  <div key={log.id} className="shd-sec-event">
                    <span className="shd-sec-event__action">{log.acao}</span>
                    <span className="shd-sec-event__user">{log.usuario_nome}</span>
                  </div>
                ))}
                {!forensics.recentAlerts?.length && (
                  <p className="shd-empty shd-empty--sm">
                    Nenhuma atividade crítica recente.
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Quick stats ── */}
        <div className="shd-card shd-card--quick">
          <div className="shd-card__head">
            <div className="shd-card__head-left">
              <span className="shd-card__icon-wrap" style={{ background: "#FFFBEB" }}>
                <Zap size={15} color="#F59E0B" />
              </span>
              <div>
                <h2 className="shd-card__title">Visão Rápida</h2>
                <p className="shd-card__sub">Números-chave do sistema</p>
              </div>
            </div>
          </div>

          <div className="shd-quick-grid">
            {[
              {
                label: "Solicitações pendentes",
                value: pending.length,
                color: pending.length > 0 ? "#EF4444" : "#10B981",
                icon:  <FileText size={14} />,
              },
              {
                label: "Clientes críticos",
                value: critical.length,
                color: critical.length > 0 ? "#F59E0B" : "#10B981",
                icon:  <AlertTriangle size={14} />,
              },
              {
                label: "Clientes ativos",
                value: loading ? "—" : stats?.activeClients ?? "—",
                color: "#10B981",
                icon:  <CheckCircle2 size={14} />,
              },
              {
                label: "Total de usuários",
                value: loading ? "—" : stats?.totalUsers ?? "—",
                color: "#8B5CF6",
                icon:  <Users size={14} />,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="shd-quick-item"
                style={{ "--qc": item.color }}
              >
                <div className="shd-quick-item__icon" style={{ color: item.color }}>
                  {item.icon}
                </div>
                <div className="shd-quick-item__val">{item.value}</div>
                <div className="shd-quick-item__label">{item.label}</div>
              </div>
            ))}
          </div>

          {lastUpdated && (
            <p className="shd-last-updated">
              <Clock size={11} />
              Atualizado às {lastUpdated.toLocaleTimeString("pt-BR")}
            </p>
          )}
        </div>

      </div>{/* /shd-grid */}
    </div>
  );
}